import {Dispatch, SetStateAction, useEffect} from "react";
import {Admission, ErrorResponseDto} from "@reside/reside-api-admission";
import {QueryVariableMap, useSafeSetState} from "@reside/ui";
import {useNavigate} from "react-router-dom";
import {useMutation} from "react-query";
import Alert from "react-s-alert";
import {
  getValidationConfig,
  Template,
  Values,
  extractTemplateSlides,
} from "@reside/forms";

import {useDashboardAdmissionsContext} from "../../../../atoms/dashboard-table/DashboardAdmissionsContext";
import {
  rememberWrittenCard,
  workflowCardFromAdmission,
} from "../../../../atoms/dashboard-table/hooks/useWorkflowsQuery";
import {getAuthorizationHeader} from "../../../../models/admin-session/selectors";
import {
  createPreflightUrl,
  usePreflightRouteParams,
  usePreflightWorkflowParam,
} from "../../route";
import {getFormValidationErrors} from "../../../../utils/formUtils";
import {useIsPreflightDirty} from "./useIsPreflightDirty";
import {PreflightSlideBlock} from "../../types";
import {useSlideUi} from "./useSlideUi";
import {
  admissionsApi,
  isMissingMandatoryChecklistItemException,
} from "../../../../services/AdmissionsService";
import {
  buildErrorMessage,
  buildResponseErrorMessage,
} from "../../../../services/api/errorHelpers";
import {
  coreValidation,
  fullValidation,
  getPreFlightCreateAdmissionPayload,
} from "../helpers/helpers";
import {getWorkflowLabel} from "../../../../atoms/start-workflow/workflowMapping";

type State = Readonly<{
  answers: Values;
  isValid: boolean;
  hasErrorsVisible: boolean;
  slides: PreflightSlideBlock[];
  hasSetInProgress: boolean;
  useCoreValidation: boolean;
}>;

export const usePreflight = ({
  template,
  admissionDraft,
  setAdmissionDraft,
  values = {},
  initialSlides = [],
  queryVariables = {},
}: {
  template: Template;
  admissionDraft: Admission;
  setAdmissionDraft: Dispatch<SetStateAction<Admission>>;
  values?: Values;
  initialSlides?: PreflightSlideBlock[];
  queryVariables?: QueryVariableMap;
}) => {
  const navigate = useNavigate();
  const {admissionId} = usePreflightRouteParams();
  const workflowParamFromUrl = usePreflightWorkflowParam();
  const workflowType = admissionDraft.workflowType ?? workflowParamFromUrl;

  const [state, setState, getState] = useSafeSetState<State>(() => ({
    answers: values,
    isValid: false,
    hasErrorsVisible: false,
    slides: initialSlides,
    hasSetInProgress: false,
    useCoreValidation: false,
  }));

  const {answers, slides} = state;

  const {setAdmissionChecklistErrors} = useDashboardAdmissionsContext();

  const {isLastSlide, uiState, uiActions} = useSlideUi({
    slideCount: slides.length,
  });

  const selectSlidesAndValidation = ({
    template,
    answers,
    useCoreValidation,
  }: {
    template: Template;
    answers?: Values;
    useCoreValidation?: boolean;
  }) => {
    const slides = extractTemplateSlides(
      template,
      answers,
    ) as PreflightSlideBlock[];

    const validated = useCoreValidation
      ? coreValidation({
          answers,
          slides,
        })
      : fullValidation({
          answers,
          slides,
        });

    return {
      answers,
      ...validated,
    };
  };

  const setAnswers = async (answers: Values, useCoreValidation?: boolean) => {
    setState(state => ({
      ...state,
      ...selectSlidesAndValidation({
        template,
        answers,
        useCoreValidation,
      }),
      useCoreValidation,
    }));

    return getState();
  };

  const setHasErrorsVisible = (hasErrorsVisible = true) => {
    setState(state => ({...state, hasErrorsVisible}));
  };

  const beforeDraft = async () => {
    setHasErrorsVisible();
    await setAnswers(
      {
        ...answers,
      },
      true,
    );
    const nextState = await getState();
    if (!nextState.isValid) {
      return null;
    }
    return nextState;
  };

  const currentSlide: PreflightSlideBlock =
    slides[uiState.activeSlideIndex] || slides[0];

  const {setDirtyFormState, dirtySlides, resetDirtyFormOnSaveSuccess} =
    useIsPreflightDirty({
      slides,
      values,
      currentSlide,
    });

  /*
   * The dashboard reads a replica that trails this write by up to a few seconds. Holding the
   * response lets the board show the workflow as saved straight away -- including when the user
   * goes back to it before replication has caught up.
   */
  const showOnDashboard = (admission: Admission) =>
    rememberWrittenCard(workflowCardFromAdmission(admission));

  const onDraftSuccess = (admission: Admission) => {
    showOnDashboard(admission);
    resetDirtyFormOnSaveSuccess();
    setAdmissionDraft(admission as any);

    const preflightUrl = createPreflightUrl({
      facilityId: admission.facilityId,
      admissionId: admission.id,
      workflow: admission.workflowType || workflowType,
    });

    if (admissionId) {
      navigate(preflightUrl, {replace: true});
    } else {
      navigate(preflightUrl);
    }

    Alert.success(
      `${getWorkflowLabel(
        admission.workflowType,
      )} data was saved successfully.`,
    );
  };

  const onDraftError = async (resp: any) => {
    Alert.error(
      await buildResponseErrorMessage(
        resp,
        "Could not save workflow data at this time. Please, try again later.",
      ),
    );
  };

  const {mutate: saveDraft, isLoading: isSavingDraft} = useMutation(
    async (nextState: State) => {
      const {data} = await admissionsApi.createAdmissionUsingPOST(
        getPreFlightCreateAdmissionPayload({
          admissionDraft,
          queryVariables,
          workflowType,
          ...nextState,
        }) as any,
        getAuthorizationHeader(),
      );
      return data;
    },
    {
      onSuccess: onDraftSuccess,
      onError: onDraftError,
    },
  );

  const {mutate: patchDraftAdmission, isLoading: isPatchingDraft} = useMutation(
    async (nextState: State) => {
      const {data} = await admissionsApi.patchAdmissionUsingPATCH(
        getPreFlightCreateAdmissionPayload({
          admissionDraft,
          queryVariables,
          workflowType,
          ...nextState,
        }) as any,
        admissionId,
        getAuthorizationHeader(),
      );
      return data;
    },
    {
      onSuccess: onDraftSuccess,
      onError: onDraftError,
    },
  );

  const beforeAdmissionSave = async () => {
    setHasErrorsVisible();
    await setAnswers(
      {
        ...answers,
      },
      false,
    );

    const nextState = await getState();

    if (!nextState.isValid) {
      return null;
    }

    return nextState;
  };

  const extractAdmissionIdFromError = (error: ErrorResponseDto) => {
    const [, admissionId] = error.path.split("/").reverse();
    return admissionId;
  };

  const {mutate: sendToResident, isLoading: isSendingToResident} = useMutation(
    async (nextState: State) => {
      const payload = getPreFlightCreateAdmissionPayload({
        admissionDraft,
        queryVariables,
        workflowType,
        ...nextState,
      }) as any;

      const {data} = admissionId
        ? await admissionsApi.patchAdmissionUsingPATCH(
            payload,
            admissionId,
            getAuthorizationHeader(),
          )
        : await admissionsApi.createAdmissionUsingPOST(
            payload,
            getAuthorizationHeader(),
          );

      const {data: updatedAdmission} =
        await admissionsApi.sendAdmissionToResidentUsingPATCH(
          data.id,
          workflowType,
          getAuthorizationHeader(),
        );

      return updatedAdmission;
    },
    {
      onSuccess: admission => {
        showOnDashboard(admission);
        resetDirtyFormOnSaveSuccess();
        setAdmissionDraft(admission as any);
        setState((state: State) => ({...state, hasSetInProgress: true}));
        const hasEmail = state.answers?.resident_email; // Avoid showing misleading alert if no email is provided

        Alert.closeAll();

        Alert.success(
          hasEmail
            ? `${getWorkflowLabel(
                admission?.workflowType,
              )} was successfully sent to resident.`
            : `${getWorkflowLabel(
                admission?.workflowType,
              )} started successfully.`,
        );

        // Do not redirect to dashboard from here because it will prevent PCC modal from being shown
      },
      onError: async (resp: any) => {
        const error: ErrorResponseDto = await resp.json();

        if (isMissingMandatoryChecklistItemException(error)) {
          setAdmissionChecklistErrors(
            admissionId ?? extractAdmissionIdFromError(error),
            getFormValidationErrors(error.validationErrors),
          );

          const [firstError] = error.validationErrors;

          if (!admissionId) {
            Alert.warning(
              "Workflow was not sent to resident. Please complete the mandatory checklist items first.",
            );
          } else {
            Alert.error(firstError.message);
          }

          navigate("/admin/dashboard");
        } else {
          Alert.error(
            buildErrorMessage(
              error,
              "Could not send workflow to resident. Please, try again later.",
            ),
          );
        }
      },
    },
  );

  const {mutate: patchInProgressAdmission, isLoading: isPatchingAdmission} =
    useMutation(
      async (nextState: State) => {
        const {data} = await admissionsApi.patchAdmissionUsingPATCH(
          getPreFlightCreateAdmissionPayload({
            admissionDraft,
            queryVariables,
            workflowType,
            ...nextState,
          }) as any,
          admissionId,
          getAuthorizationHeader(),
        );
        return data;
      },
      {
        onSuccess: admission => {
          showOnDashboard(admission);
          setAdmissionDraft(admission as any);
          Alert.closeAll();
          Alert.success(
            `${getWorkflowLabel(
              admission?.workflowType,
            )} data was saved successfully.`,
          );

          resetDirtyFormOnSaveSuccess();
        },
        onError: async (resp: any) => {
          Alert.error(
            await buildResponseErrorMessage(
              resp,
              "Could not save workflow. Please, try again later.",
            ),
          );
        },
      },
    );

  const createDraft = async () => {
    const nextState = await beforeDraft();
    if (!nextState) return;
    saveDraft(nextState);
  };

  const patchDraft = async () => {
    const nextState = await beforeDraft();
    if (!nextState) return;
    patchDraftAdmission(nextState);
  };

  const createAdmission = async () => {
    const nextState = await beforeAdmissionSave();
    if (!nextState) return;
    sendToResident(nextState);
  };

  const patchAdmission = async () => {
    const nextState = await beforeAdmissionSave();
    if (!nextState) return;
    patchInProgressAdmission(nextState);
  };

  useEffect(() => {
    if (template && template.children.length > 0) {
      setState(state => {
        return {
          ...state,
          ...selectSlidesAndValidation({
            template,
            answers,
            useCoreValidation: state.useCoreValidation,
          }),
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  const {validate, validationMessages, validationRules} = getValidationConfig({
    children: currentSlide?.children ?? [],
    answers,
  });

  return {
    ...state,
    draft: {...admissionDraft, answers} as any as Admission,
    isLastSlide,
    uiState,
    uiActions,
    setAnswers,
    getAnswers: async () => (await getState())?.answers,
    /** Data displayed in current slide. */
    currentSlide,
    /** Determine, by index, which slide is active at any moment. */
    currentSlideIndex: uiState.activeSlideIndex,
    validate,
    saveDraft,
    patchDraft,
    isSavingDraft,
    isPatchingDraft,
    sendToResident,
    isSendingToResident,
    patchAdmission,
    isPatchingAdmission,
    validationMessages,
    validationRules,
    createDraft,
    createAdmission,
    setDirtyFormState,
    dirtySlides,
    getIsCoreValidation: async () => (await getState())?.useCoreValidation,
  };
};
